<template>
  <div class="relative">
    <!-- Product Image -->
    <v-img
      v-if="product.imageUrl"
      :src="product.imageUrl"
      :lazy-src="product.imageUrl"
      height="220"
      contain
      class="rounded-t-xl"
    >
      <template v-slot:placeholder><div></div></template>

      <!-- 🟢 NEW PRODUCT Badge -->
      <div v-if="isNewProduct" class="new-product-badge">
        <v-icon class="mr-1" size="20">mdi-flash</v-icon>
        New Product
      </div>

      <!-- 🟡 LAST ORDER Badge -->
      <v-chip
        v-if="product.lastPurchasedAt"
        small
        color="amber darken-3"
        text-color="white"
        class="last-order-chip"
        @click.stop="openLastOrderDialog"
      >
        <v-icon left small>mdi-history</v-icon>
        Last Ordered
      </v-chip>
    </v-img>

    <v-img
      v-else
      src="@/assets/images/photoComingSoon.png"
      lazy-src="@/assets/images/photoComingSoon.png"
      height="220"
      contain
      class="rounded-t-xl"
    >
      <div v-if="isNewProduct" class="new-product-badge">
        <v-icon class="mr-1" size="20">mdi-flash</v-icon>
        New Product
      </div>

      <!-- 🟡 LAST ORDER Badge -->
      <v-chip
        v-if="product.lastPurchasedAt"
        small
        color="amber darken-3"
        text-color="white"
        class="last-order-chip"
        @click.stop="openLastOrderDialog"
      >
        <v-icon left small>mdi-history</v-icon>
        Last Ordered
      </v-chip>
    </v-img>

    <!-- Product Details -->
    <v-card-title class="pb-1 font-weight-bold title-scroll">
      {{ product.name }}
    </v-card-title>

    <v-card-title v-if="product.note" class="pb-1 text-caption">
      {{ product.note }}
    </v-card-title>
    <v-divider v-if="product.note"></v-divider>

    <v-card-text class="pt-0 pb-3">
      <v-row class="align-center">
        <v-col cols="6">
          <div v-if="product.dynamicPrice === product.price">
            <span class="font-weight-bold text-red">
              {{ product.dynamicPrice | currency }} *
            </span>
          </div>
          <div v-else-if="product.unitCountInBox && product.unitCountInBox > 0">
            <span class="font-weight-medium">Per Unit: </span>
            <span class="font-weight-bold">
              {{ (product.price / product.unitCountInBox) | currency }}
            </span>
          </div>
          <div v-else>
            <span class="font-weight-medium">Price: </span>
            <span class="font-weight-bold">{{ product.price | currency }}</span>
          </div>
        </v-col>
        <v-col cols="6" class="text-right" v-if="shouldShowStock">
          <div
            class="text-caption text-grey-darken-2"
            v-if="product.balance > 0"
          >
            Stock:
            <span class="font-weight-bold">{{ product.balance }}</span>
          </div>
          <v-chip v-else small color="#FDE8E4" label text-color="red">
            Out of Stock - {{ product.balance }}
          </v-chip>
          <div
            v-if="product.unitCountInBox"
            class="text-caption text-grey-darken-1"
          >
            {{ product.unitCountInBox }} per box
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider class="mx-4"></v-divider>

    <!-- 🟢 Dialog: Last 3 Orders -->
    <v-dialog v-model="showLastOrderDialog" max-width="400">
      <v-card>
        <v-card-title class="headline font-weight-bold">
          <v-icon left color="amber darken-3">mdi-history</v-icon>
          Last 3 Orders
        </v-card-title>

        <v-card-text>
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">Date</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Invoice</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(entry, index) in product.purchaseHistory"
                :key="index"
              >
                <td>{{ formatDate(entry.date) }}</td>
                <td class="text-right">{{ entry.qty }}</td>
                <td class="text-right">{{ entry.invoiceNumber }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="showLastOrderDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ProductCard",
  props: ["product", "shouldShowStock"],
  data() {
    return {
      showLastOrderDialog: false,
    };
  },
  computed: {
    isNewProduct() {
      if (!this.product.createdAt) return false;
      const created = new Date(this.product.createdAt);
      const now = new Date();
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      return diffDays <= 30; // 👈 within 30 days = "New Product"
    },
    formattedLastDate() {
      if (!this.product.lastPurchasedAt) return "";
      const date = new Date(this.product.lastPurchasedAt);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  methods: {
    openLastOrderDialog() {
      this.showLastOrderDialog = true;
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.relative {
  position: relative;
}

/* ✨ New Product Badge Overlay */
.new-product-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #43a047, #66bb6a);
  color: white;
  font-weight: 700;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  box-shadow: 0 0 10px rgba(67, 160, 71, 0.8);
  animation: pulse 1.5s infinite;
}

/* 🟡 Last Order Chip */
.last-order-chip {
  position: absolute;
  top: 12px;
  right: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.25);
}

/* 🌀 Pulse animation */
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 10px rgba(67, 160, 71, 0.6);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(67, 160, 71, 1);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 10px rgba(67, 160, 71, 0.6);
  }
}
</style>